import React, { useState } from 'react';
import { images, calendlyLink, services, testimonials } from '../mock';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const HomePage = () => {
  const [showMoreHypnotherapy, setShowMoreHypnotherapy] = useState(false);

  return (
    <div>
      {/* Hero Section with Full Background Image */}
      <section style={{
        backgroundImage: `url(${images.heroBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative'
      }} className="fade-in">
        {/* Overlay for text readability */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to right, rgba(255, 254, 242, 0.92) 0%, rgba(255, 254, 242, 0.75) 50%, rgba(255, 254, 242, 0.5) 100%)'
        }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ maxWidth: '600px' }}>
            <h1 className="hero-large" style={{ marginBottom: '24px' }}>
              Unlock Your Mind, Transform Your Life
            </h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '32px', fontStyle: 'italic' }}>
              Break free from old patterns, tap into your subconscious,<br />
              and become the version of you that you've always dreamed of.
            </p>
            <a
              href={calendlyLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: 'transparent',
                color: 'var(--interactive-base)',
                border: '1px solid var(--interactive-base)',
                borderRadius: '0px',
                padding: '19px 23px',
                minWidth: '210px',
                height: '60px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              className="btn-primary"
            >
              I'm Ready
            </a>
          </div>
        </div>
      </section>

      {/* What Is Hypnotherapy */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ marginBottom: '32px', maxWidth: '700px' }}>
            What is hypnotherapy?
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', marginBottom: '24px' }}>
            Hypnotherapy is a guided process that connects you to your subconscious mind — the part of you where habits, emotions, memories, and beliefs are stored.
          </p>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', marginBottom: '24px' }}>
            In a deeply relaxed and focused state, your mind becomes more open, receptive, and aware. This allows you to gently rewire patterns, release emotional blocks, and create lasting change from the inside out.
          </p>
          <p className="body-large" style={{ color: 'var(--text-primary)', maxWidth: '800px', fontWeight: 500, marginBottom: '24px' }}>
            It's not about losing control. It's about accessing it.
          </p>
          
          {!showMoreHypnotherapy && (
            <button
              onClick={() => setShowMoreHypnotherapy(true)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--interactive-base)',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                padding: '8px 0',
                textDecoration: 'underline'
              }}
            >
              more...
            </button>
          )}
          
          {showMoreHypnotherapy && (
            <div style={{ marginTop: '24px', maxWidth: '800px' }}>
              <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                Hypnotherapy is a gentle, focused form of therapy that uses guided relaxation, breathwork, and visualization to access the subconscious mind — the place where your automatic behaviors and emotional responses live.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                This relaxed state, often called a trance state, is completely natural. In fact, you enter similar states every day — like when you drive somewhere and don't remember parts of the journey because your mind was elsewhere.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                In hypnosis, this heightened concentration allows the subconscious to become more accessible. Positive suggestions and visualizations are received more deeply, making it easier to:
              </p>
              <ul style={{ listStyle: 'none', padding: 0, marginBottom: '24px' }}>
                <li style={{ marginBottom: '8px' }}>• Shift destructive thought patterns</li>
                <li style={{ marginBottom: '8px' }}>• Reprogram habits</li>
                <li style={{ marginBottom: '8px' }}>• Release emotional pain</li>
                <li style={{ marginBottom: '8px' }}>• Heal from unresolved experiences</li>
                <li style={{ marginBottom: '8px' }}>• Access inner resources you didn't know you had</li>
              </ul>
              <p className="body-large" style={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '24px' }}>
                We are not our stories.
              </p>
              <p className="body-large" style={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '24px' }}>
                And when we access the subconscious, we gain the power to rewrite them.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Hypnotherapy is now widely recognized and used in modern clinical settings to support anxiety, phobias, addiction, pain management, and emotional trauma — and its roots go back thousands of years to ancient healing traditions in Egypt, Greece, and India, where trance states were used for spiritual and physical restoration.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Why Hypnotherapy */}
      <section className="section-padding">
        <div className="container">
          <h2 className="heading-1" style={{ marginBottom: '32px' }}>
            Why hypnotherapy?
          </h2>
          <div style={{ maxWidth: '800px' }}>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontStyle: 'italic' }}>
              Most change fails because it happens at the conscious level. Willpower lives in the conscious mind. But habits and belief systems live in the subconscious.
            </p>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontStyle: 'italic' }}>
              Hypnotherapy works at the root. By bypassing surface-level thinking, we access the deeper programming driving your behaviors, fears, and emotional responses. Instead of managing symptoms, we transform the source.
            </p>
            
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontStyle: 'italic' }}>It is:</p>
            <ul style={{ listStyle: 'none', padding: 0, marginBottom: '24px', marginLeft: '16px', fontStyle: 'italic' }}>
              <li style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>• Natural</li>
              <li style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>• Non-invasive</li>
              <li style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>• Holistic</li>
              <li style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>• Deeply empowering</li>
            </ul>

            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontStyle: 'italic' }}>
              For thousands of years, altered states of consciousness have been used for healing and insight. Ancient Greek "sleep temples," Egyptian dream incubation practices, and yogic meditation traditions all relied on trance states to facilitate transformation.
            </p>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontStyle: 'italic' }}>
              Today, neuroscience confirms what ancient wisdom understood — the subconscious mind plays a powerful role in shaping behavior, emotion, and even physical response. When we work with it intentionally, change becomes faster.
            </p>
            <p className="body-large" style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              And more sustainable.
            </p>
          </div>
        </div>
      </section>

      {/* How Does It Work */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ marginBottom: '32px' }}>
            How Does It Work?
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            <div style={{ padding: '32px', background: 'var(--bg-primary)', borderRadius: '0px' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>1. Deep Relaxation</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                You're guided into a deeply relaxed state where your conscious mind quiets and your subconscious becomes receptive.
              </p>
            </div>
            <div style={{ padding: '32px', background: 'var(--bg-primary)', borderRadius: '0px' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>2. Subconscious Reprogramming</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Through carefully crafted suggestions and techniques, we reprogram limiting beliefs and install new, empowering patterns.
              </p>
            </div>
            <div style={{ padding: '32px', background: 'var(--bg-primary)', borderRadius: '0px' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>3. Lasting Transformation</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Changes made at the subconscious level are lasting and automatic, creating effortless shifts in your thoughts, feelings, and behaviors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Can It Be Used For */}
      <section className="section-padding">
        <div className="container">
          <h2 className="heading-1" style={{ marginBottom: '24px' }}>
            What can it be used for?
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', marginBottom: '48px' }}>
            At GetLucid, hypnotherapy is tailored to help you shift the root cause — not just manage the symptoms. Whether you're navigating stress, breaking patterns, or stepping into your next level, your subconscious holds the key.
          </p>
          
          {/* Detailed Categories */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {/* Anxiety & Stress Relief */}
            <div style={{ padding: '32px', background: 'var(--bg-secondary)' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Anxiety & Stress Relief</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                If your mind feels "always on," you overthink, or stress is affecting your sleep and daily life.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Anxiety support', 'Panic attacks', 'Chronic stress & burnout', 'Overthinking & racing thoughts', 'Nervous tension', 'Social anxiety', 'High-functioning anxiety'].map((item, i) => (
                  <li key={i} style={{ marginBottom: '6px', fontSize: '14px', color: 'var(--text-secondary)' }}>• {item}</li>
                ))}
              </ul>
            </div>

            {/* Confidence & Self-Esteem */}
            <div style={{ padding: '32px', background: 'var(--bg-secondary)' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Confidence & Self-Esteem</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                If fear is holding you back or self-doubt is limiting your potential.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Low self-confidence', 'Imposter syndrome', 'Public speaking confidence', 'Self-worth & self-image', 'Decision-making clarity', 'Assertiveness & boundaries'].map((item, i) => (
                  <li key={i} style={{ marginBottom: '6px', fontSize: '14px', color: 'var(--text-secondary)' }}>• {item}</li>
                ))}
              </ul>
            </div>

            {/* Trauma & Emotional Healing */}
            <div style={{ padding: '32px', background: 'var(--bg-secondary)' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Trauma & Emotional Healing</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                If past experiences still affect how you think, feel, or react.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Trauma processing', 'PTSD support (complementary)', 'Childhood wounds', 'Emotional triggers', 'Guilt, shame & regret', 'Grief & loss', 'Divorce recovery'].map((item, i) => (
                  <li key={i} style={{ marginBottom: '6px', fontSize: '14px', color: 'var(--text-secondary)' }}>• {item}</li>
                ))}
              </ul>
            </div>

            {/* Sleep & Insomnia */}
            <div style={{ padding: '32px', background: 'var(--bg-secondary)' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Sleep & Insomnia</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                If your body is tired but your mind won't switch off.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Insomnia', 'Difficulty falling asleep', 'Waking during the night', 'Restless sleep', 'Stress-related sleep issues'].map((item, i) => (
                  <li key={i} style={{ marginBottom: '6px', fontSize: '14px', color: 'var(--text-secondary)' }}>• {item}</li>
                ))}
              </ul>
            </div>

            {/* Breaking Habits & Addictions */}
            <div style={{ padding: '32px', background: 'var(--bg-secondary)' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Breaking Habits & Addictions</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                If you feel stuck in cycles you can't seem to break.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Stop smoking', 'Stop vaping', 'Emotional eating', 'Weight management', 'Sugar cravings', 'Nail biting', 'Procrastination', 'Addiction support (complementary)'].map((item, i) => (
                  <li key={i} style={{ marginBottom: '6px', fontSize: '14px', color: 'var(--text-secondary)' }}>• {item}</li>
                ))}
              </ul>
            </div>

            {/* Fears & Phobias */}
            <div style={{ padding: '32px', background: 'var(--bg-secondary)' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Fears & Phobias</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                If fear limits your choices or freedom.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Fear of flying', 'Driving anxiety', 'Social fears', 'Medical or dental phobia', 'Fear of failure', 'Fear of rejection'].map((item, i) => (
                  <li key={i} style={{ marginBottom: '6px', fontSize: '14px', color: 'var(--text-secondary)' }}>• {item}</li>
                ))}
              </ul>
            </div>

            {/* Performance & Focus */}
            <div style={{ padding: '32px', background: 'var(--bg-secondary)' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Performance & Focus</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                If you want steady confidence under pressure.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Sports performance', 'Business performance', 'Leadership mindset', 'Exam confidence', 'Creativity blocks', 'Productivity & motivation'].map((item, i) => (
                  <li key={i} style={{ marginBottom: '6px', fontSize: '14px', color: 'var(--text-secondary)' }}>• {item}</li>
                ))}
              </ul>
            </div>

            {/* Relationships & Emotional Regulation */}
            <div style={{ padding: '32px', background: 'var(--bg-secondary)' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Relationships & Emotional Regulation</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                If communication breaks down or emotions feel hard to manage.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Anger management', 'Relationship conflict', 'Attachment patterns', 'Emotional regulation', 'Codependency', 'Trust & intimacy issues'].map((item, i) => (
                  <li key={i} style={{ marginBottom: '6px', fontSize: '14px', color: 'var(--text-secondary)' }}>• {item}</li>
                ))}
              </ul>
            </div>

            {/* Spiritual & Inner Growth */}
            <div style={{ padding: '32px', background: 'var(--bg-secondary)' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Spiritual & Inner Growth</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                For those ready to explore deeper awareness.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Past life regression (experience-based)', 'Abundance mindset', 'Law of attraction & belief work', 'Identity evolution', 'Purpose clarity'].map((item, i) => (
                  <li key={i} style={{ marginBottom: '6px', fontSize: '14px', color: 'var(--text-secondary)' }}>• {item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h1 className="hero-medium" style={{ marginBottom: '48px', fontSize: '36px' }}>
            Services
          </h1>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {services.map((service) => (
              <div key={service.id} style={{
                background: 'var(--bg-primary)',
                borderRadius: '0px',
                overflow: 'hidden',
                cursor: 'pointer'
              }} className="hover-lift">
                <img
                  src={service.image}
                  alt={service.title}
                  style={{
                    width: '100%',
                    height: '220px',
                    objectFit: 'cover'
                  }}
                />
                <div style={{ padding: '32px' }}>
                  <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                    {service.title}
                  </h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                    {service.shortDescription}
                  </p>
                  <Link
                    to={`/services/${service.id}`}
                    style={{
                      color: 'var(--interactive-base)',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    Learn More <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="section-padding">
        <div className="container">
          <h2 className="heading-1" style={{ marginBottom: '48px' }}>
            What Clients Say
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {testimonials.slice(0, 3).map((testimonial) => (
              <div key={testimonial.id} style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px'
              }}>
                <p className="body-regular" style={{ marginBottom: '24px', fontStyle: 'italic' }}>
                  "{testimonial.quote}"
                </p>
                <p className="body-small" style={{ fontWeight: 500 }}>
                  — {testimonial.author}
                </p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/testimonials" style={{
              color: 'var(--interactive-base)',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              Read More Testimonials <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ textAlign: 'center', maxWidth: '700px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
            Awaken Your Potential
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
            Step into a life of limitless possibilities.
          </p>
          <a
            href={calendlyLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: 'transparent',
              color: 'var(--interactive-base)',
              border: '1px solid var(--interactive-base)',
              borderRadius: '0px',
              padding: '19px 23px',
              minWidth: '210px',
              height: '60px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            className="btn-primary"
          >
            Book Your Free Discovery Session
          </a>
        </div>
      </section>

      <style>{`
        .btn-primary:hover {
          background: var(--interactive-base);
          color: var(--bg-primary);
        }
        @media (max-width: 1024px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default HomePage;
